import React from "react";
import { Clock } from "lucide-react";
import { fileIcons } from "../../../utils/icon-mimetypes";
import { resolveDeliverableMimetype } from "./utils";
import FileBg from "../../../generators/fileBGGenerator";

const STATUS_BADGE_STYLES = {
  pending: { bg: "#fff7e6", text: "#b45309" },
  approved: { bg: "#e6f4ea", text: "#137333" },
  rejected: { bg: "#fce8e6", text: "#c5221f" },
  downloaded: { bg: "#e0f2fe", text: "#0369a1" },
};

const hexToRgba = (hex, alpha) => {
  let r = 0,
    g = 0,
    b = 0;
  if (!hex) return `rgba(0,0,0,${alpha})`;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function formatLastUpdated(item) {
  const raw = item.updatedAt || item.updated_at || item.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function DeliverableCard({
  item,
  theme,
  onEdit,
  onDelete,
  onOpen,
  onResubmit,
}) {
  const resolvedMime = resolveDeliverableMimetype(item);
  const resolvedIcon =
    fileIcons[resolvedMime] ||
    `${process.env.PUBLIC_URL}/assets/defaulticon.webp`;
  const lastUpdated = formatLastUpdated(item);
  const rawStatus = (item.status || "pending").toLowerCase();
  const displayStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);
  const badge = STATUS_BADGE_STYLES[rawStatus] || STATUS_BADGE_STYLES.pending;
  const isRejected = rawStatus === "rejected";
  const resolvedColor =
    item.color ||
    item.metadata?.color ||
    (item.origin === "local" ? "#9CA3AF" : "#5E5ADB");

  return (
    <div
      onClick={() => onOpen(item, resolvedMime)}
      style={{
        background: theme.cardBg,
        backgroundColor: hexToRgba(resolvedColor, 0.08),
        backgroundImage: FileBg({
          gradientColors: [
            resolvedColor,
            resolvedColor,
            resolvedColor,
            resolvedColor,
          ],
        }),
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundBlendMode: "lighten",
        border: `1px solid ${theme.border}`,
        borderRadius: "12px",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        cursor: "pointer",
        transition: "transform 0.2s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
        }}
      >
        <img
          src={resolvedIcon}
          alt="file type icon"
          style={{
            width: "36px",
            height: "36px",
            objectFit: "contain",
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: theme.textMain,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={item.name}
            >
              {item.name}
            </div>
            <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
              <button
                type="button"
                aria-label="Edit deliverable"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#5E5ADB",
                  fontSize: "11px",
                  cursor: "pointer",
                }}
              >
                ✏️
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#d93025",
                  fontSize: "11px",
                  cursor: "pointer",
                }}
              >
                🗑️
              </button>
            </div>
          </div>
          <div
            style={{
              fontSize: "11px",
              color: theme.textSub,
              marginTop: "2px",
            }}
          >
            Origin: {item.origin === "local" ? "Device" : "Org Vault"}
          </div>
          {lastUpdated && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                color: theme.textSub,
                marginTop: "2px",
              }}
            >
              <Clock size={11} />
              <span>Updated {lastUpdated}</span>
            </div>
          )}
        </div>
      </div>
      {/* Price and progress info row parameters remain matching previous configurations */}
      <div
        style={{
          borderTop: `1px solid ${theme.border}`,
          paddingTop: "10px",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          fontSize: "12px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <span style={{ color: theme.textSub }}>Paywall: </span>
            <span
              style={{
                fontWeight: "600",
                color: item.paywallPrice > 0 ? "#137333" : theme.textMain,
              }}
            >
              {item.paywallPrice > 0 ? `$${item.paywallPrice}` : "Free"}
            </span>
          </div>
          <div>
            <span style={{ color: theme.textSub }}>Size: </span>
            <span style={{ fontWeight: "600", color: theme.textMain }}>
              {item.size || "—"}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
          }}
        >
          <span style={{ color: theme.textSub }}>Preview Constraint:</span>
          <span style={{ fontWeight: "600", color: theme.textMain }}>
            {item.previewLimit > 0 ? `${item.previewLimit} pages` : "Full access"}
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <span
          style={{
            alignSelf: "flex-start",
            background: badge.bg,
            color: badge.text,
            padding: "3px 10px",
            borderRadius: "20px",
            fontSize: "11px",
            fontWeight: "600",
            display: "inline-block",
          }}
        >
          {displayStatus}
        </span>

        {isRejected && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onResubmit?.(item);
            }}
            style={{
              background: "#5E5ADB",
              color: "#fff",
              border: "none",
              padding: "4px 10px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            Resubmit
          </button>
        )}
      </div>
    </div>
  );
}
